import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  Card,
  CardBody,
  Container,
  Badge,
  Button,
  UncontrolledTooltip,
  Modal,
  ModalHeader,
  ModalBody,
} from "reactstrap";
import { useLocation, useNavigate } from "react-router-dom";
import ReactBsTable from "components/Tables/ReactBsTable";
import DynamicFormModal from "components/Modal/Form";
import { insertionOrderFormFields } from "variables/formFields";
import { INSERTION_ORDER_STATUS_LABELS } from "variables/constants";
import Breadcrumbs from "components/BreadCrumb/Breadcrumbs";
import NotificationAlert from "react-notification-alert";
import { getInsertionOrderHistory } from "actions/insertionOrder";
import { download_insertion_order } from "API";
import moment from "moment";
import { createInsertionOrder } from "actions/insertionOrder";
import { getPublisherList } from "actions/publisher";
import { showAlert } from "components/Common/commonFunctions";
import { deleteInsertionOrder } from "actions/insertionOrder";
import { getInsertionOrderList } from "actions/insertionOrder";

const sortFieldMap = {
  name: "Name",
  startDate: "StartDate",
  endDate: "EndDate",
  version: "Version",

};

function InsertionOrderDetails() {
  const location = useLocation();
  const navigate = useNavigate();
  const publisher = location.state || null;
  const ioParams = location.state || null;
  const [ioData, setIoData] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [showLoader, setShowLoader] = useState(false);
  const [pageNum, setPageNum] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortConfig, setSortConfig] = useState({
    dataField: "",
    sortField: "",
    sortOrder: "",
  });
  const [dropDownData, setDropDownData] = useState({
    publisherData: {
      options: [],
      pageNum: 1,
      searchString: "",
      totalRecords: 0,
    },
  });
  // 🔹 Fixed preview states
  const [previewModal, setPreviewModal] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewTitle, setPreviewTitle] = useState("");
  const [currentRow, setCurrentRow] = useState(null);
  const disabledPublisherDropdown = true;
  const notificationAlertRef = useRef(null);

  const [formState, setFormState] = useState({
    values: { status: true },
    errors: {},
  });

  const [totalRecords, setTotalRecords] = useState(0);

  const isEditMode = !!selected;

  const notify = (type, message) => {
    notificationAlertRef.current?.notificationAlert({
      place: "tr",
      message: <div>{message}</div>,
      type,
      icon: "tim-icons icon-bell-55",
      autoDismiss: 5,
    });
  };

  //==> Get IO Details Api with pagination and sorting
  const getInsertionOrderDetailsByID = useCallback(async () => {
    try {
      setShowLoader(true);
      const res = await getInsertionOrderHistory(
        ioParams?.publisher?.publisherID,
        sortConfig.sortField,
        sortConfig.sortOrder,
        pageNum,
        pageSize
      );
      if (res.code === 200) {
        setIoData(res?.data.InsertionOrder || []);
        setTotalRecords(res?.data?.totalRecords || 0);
      } else {
        const message = Array.isArray(res?.message)
          ? res?.message[0]?.Msg
          : res?.message || "Something went wrong";
        notify("danger", message);
      }
    } catch (error) {
      console.error("Error fetching insertion order history:", error);
    } finally {
      setShowLoader(false);
    }
  }, [ioParams?.publisher?.publisherID, sortConfig.sortField, sortConfig.sortOrder, pageNum, pageSize]);

  useEffect(() => {
    getInsertionOrderDetailsByID();
  }, [getInsertionOrderDetailsByID]);

  const getDropDownData = async () => {
    // Fetch any dropdown data needed for the Insertion Orders tab
    try {
      const publisherRes = await getPublisherList();
      const publisherData =
        publisherRes?.code === 200
          ? (publisherRes?.data?.Publisher || []).map((item) => ({
              label: item?.companyName || "",
              value: item?.ID,
            }))
          : [];
      setDropDownData((prev) => ({
        ...prev,
        publisherData: {
          ...prev.publisherData,
          options: publisherData,
          totalRecords: publisherRes.totalRecords,
        },
      }));
    } catch (error) {}
  };

  // Handle page change
  const handlePageChange = (page) => {
    setPageNum(page);
  };

  // Handle size per page change
  const handleSizePerPageChange = (e) => {
    const newSize = Number(e.target.value);
    setPageSize(newSize);
    setPageNum(1);
  };

  // Handle sort change
  const handleSortChange = (dataField, sortOrder) => {
    const mappedSortField = sortFieldMap[dataField] || dataField;
    setSortConfig({ dataField, sortField: mappedSortField, sortOrder });
    setPageNum(1);
  };

  // Download IO function
  const downloadIO = async (row) => {
    try {
      const response = await download_insertion_order(row.ID);

      const blob = new Blob([response.data], {
        type: "application/pdf",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      const filename =
        row.fileName && row.fileName.endsWith(".pdf")
          ? row.fileName
          : `${row.fileName || "insertion-order"}.pdf`;

      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);

      notify("success", "PDF downloaded successfully");
    } catch (err) {
      console.error(err);
      notify("danger", "Failed to download PDF");
    }
  };

  // Open preview modal
  const openPreviewModal = (row, e) => {
    if (e) e.stopPropagation();
    if (!row?.fileUrl) {
      notify("warning", "No document available for preview");
      return;
    }

    setCurrentRow(row);
    setPreviewUrl(row.fileUrl);
    setPreviewTitle(row.fileName || "Document Preview");
    setPreviewModal(true);
  };

  // Close preview modal
  const closePreviewModal = () => {
    setPreviewModal(false);
    setPreviewUrl("");
    setPreviewTitle("");
    setCurrentRow(null);
  };

  //  const getInsertionOrderListData = async () => {
  //     try {
  //       const res = await getInsertionOrderList();
  //       if (res.code === 200) {
  //         setData({
  //           data: res?.data.InsertionOrder,
  //           totalRecords: res?.data.totalRecords,
  //         });
  //       } else {
  //         const message = Array.isArray(res?.message)
  //           ? res?.message[0]?.Msg
  //           : res?.message || "Something went wrong";
  //         notify("danger", message);
  //       }
  //     } catch (error) {
  //       console.error(error);
  //     }
  //   };

  // Download from preview modal
  const downloadFromPreview = () => {
    if (currentRow) {
      downloadIO(currentRow);
    }
  };

  /* COLUMNS with sorting */
  const ioColumns = [
    // { text: "Sr. No.", formatter: (_, __, i) => (pageNum - 1) * pageSize + i + 1 },
    {
      dataField: "name",
      text: "IO Name",
      sort: true,
      sortCaret: (order) => {
        if (!order) {
          return (
            <span className="sort-caret" style={{ marginLeft: "8px" }}>
              <i className="fa fa-sort" />
            </span>
          );
        }
        return order === "asc" ? (
          <span className="sort-caret" style={{ marginLeft: "8px" }}>
            <i className="fa fa-sort-up" />
          </span>
        ) : (
          <span className="sort-caret" style={{ marginLeft: "8px" }}>
            <i className="fa fa-sort-down" />
          </span>
        );
      },
    },
    {
      dataField: "startDate",
      text: "Start Date",
      formatter: (cell) =>
        cell !== "" ? moment(cell).format("DD MMM YYYY") : "-",
      sort: true,
      sortCaret: (order) => {
        if (!order) {
          return (
            <span className="sort-caret" style={{ marginLeft: "8px" }}>
              <i className="fa fa-sort" />
            </span>
          );
        }
        return order === "asc" ? (
          <span className="sort-caret" style={{ marginLeft: "8px" }}>
            <i className="fa fa-sort-up" />
          </span>
        ) : (
          <span className="sort-caret" style={{ marginLeft: "8px" }}>
            <i className="fa fa-sort-down" />
          </span>
        );
      },
    },
    {
      dataField: "endDate",
      text: "End Date",
      formatter: (cell) =>
        cell !== "" ? moment(cell).format("DD MMM YYYY") : "-",
      sort: true,
      sortCaret: (order) => {
        if (!order) {
          return (
            <span className="sort-caret" style={{ marginLeft: "8px" }}>
              <i className="fa fa-sort" />
            </span>
          );
        }
        return order === "asc" ? (
          <span className="sort-caret" style={{ marginLeft: "8px" }}>
            <i className="fa fa-sort-up" />
          </span>
        ) : (
          <span className="sort-caret" style={{ marginLeft: "8px" }}>
            <i className="fa fa-sort-down" />
          </span>
        );
      },
    },
    {
      dataField: "version",
      text: "Version",
      sort: true,
      sortCaret: (order) => {
        if (!order) {
          return (
            <span className="sort-caret" style={{ marginLeft: "8px" }}>
              <i className="fa fa-sort" />
            </span>
          );
        }
        return order === "asc" ? (
          <span className="sort-caret" style={{ marginLeft: "8px" }}>
            <i className="fa fa-sort-up" />
          </span>
        ) : (
          <span className="sort-caret" style={{ marginLeft: "8px" }}>
            <i className="fa fa-sort-down" />
          </span>
        );
      },
    },
    {
      text: "Document",
      formatter: (_, row) =>
        row?.fileUrl ? (
          <span
            className="text-info pointer"
            onClick={(e) => openPreviewModal(row, e)}
            style={{ cursor: "pointer", textDecoration: "underline" }}
          >
            {row.fileName || "View Document"}
          </span>
        ) : (
          "-"
        ),
    },
    {
      dataField: "documentStatus",
      text: "Status",
      formatter: (cell) => (
        <Badge color={cell === "SIGNED" ? "success" : "warning"}>{cell}</Badge>
      ),
    },
    {
      text: "Actions",
      formatter: (_, row) => (
        <div className="d-flex align-items-center">
          {/* View Button */}
          <a
            id={`view${row.ID}`}
            className="table-action table-action-info mr-2"
            onClick={(e) => {
              e.stopPropagation();
              openPreviewModal(row, e);
            }}
          >
            <i className="fas fa-eye" />
          </a>
          <UncontrolledTooltip target={`view${row.ID}`}>
            View Document
          </UncontrolledTooltip>

          {/* Download Button */}
          <a
            id={`download${row.ID}`}
            className="table-action table-action-success"
            onClick={(e) => {
              e.stopPropagation();
              downloadIO(row);
            }}
          >
            <i className="fas fa-download" />
          </a>
          <UncontrolledTooltip target={`download${row.ID}`}>
            Download Document
          </UncontrolledTooltip>
          {row.status === "ACTIVE" && (
            <>
              <a
                id={`delete${row.id}`}
                className="table-action table-action-delete mr-2"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteAlert(row);
                }}
              >
                <i className="fas fa-trash" />
              </a>
              <UncontrolledTooltip target={`delete${row.id}`}>
                Delete IO
              </UncontrolledTooltip>
            </>
          )}
        </div>
      ),
    },
  ];

  const handleDeleteAlert = (row) => {
    showAlert({
      title: `Are you sure you want to delete this Insertion Order?`,
      message: `Insertion Order "${row?.name}" will be deleted.`,
      type: "warning",
      // confirmStyle: "secondary",
      cancelStyle: "secondary",
      confirmText: "Delete",
      cancelText: "Cancel",
      onConfirm: () => deleteInsertionOrderById(row),
      onCancel: () => {},
    });
  };

  const deleteInsertionOrderById = async (data) => {
    try {
      const res = await deleteInsertionOrder(data?.ID);
      if (res.code === 200) {
        const message = Array.isArray(res?.message)
          ? res?.message[0]?.Msg
          : res?.message || "Insertion Order deleted successfully.";
        notify("success", message);
        getInsertionOrderDetailsByID();
      } else {
        const message = Array.isArray(res?.message)
          ? res?.message[0]?.Msg
          : res?.message || "Something went wrong";
        notify("danger", message);
      }
    } catch (error) {
      notify("danger", error?.message);
    }
  };

  /* ROW CLICK → DETAILS PAGE */
  const handleRowClick = (row) => {
    navigate(`/admin/insertion-orders/details`, {
      state: row,
    });
  };

  //==> FIXED: handleChange function - handles both event objects and direct value calls
  const handleChange = (fieldOrEvent, valueOrField, maybeValue) => {
    // Case 1: Called as handleChange(fieldName, value) from DynamicFormModal
    if (typeof fieldOrEvent === 'string') {
      const fieldName = fieldOrEvent;
      const fieldValue = valueOrField;
      
      setFormState((prev) => {
        const updatedValues = {
          ...prev.values,
          [fieldName]: fieldValue,
        };

        const { startDate, endDate } = updatedValues;

        // If both dates exist and startDate > endDate → clear endDate
        if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
          updatedValues.endDate = "";
        }

        return {
          values: updatedValues,
          errors: {
            ...prev.errors,
            [fieldName]: "",
          },
        };
      });
      return;
    }

    // Case 2: Called as handleChange(e) from normal input events
    const event = fieldOrEvent;
    if (event && event.target) {
      const { name, value } = event.target;
      
      setFormState((prev) => {
        const updatedValues = {
          ...prev.values,
          [name]: value,
        };

        const { startDate, endDate } = updatedValues;

        // If both dates exist and startDate > endDate → clear endDate
        if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
          updatedValues.endDate = "";
        }

        return {
          values: updatedValues,
          errors: {
            ...prev.errors,
            [name]: "",
          },
        };
      });
      return;
    }

    // Case 3: Fallback - do nothing
    console.warn("handleChange called with unexpected arguments:", fieldOrEvent, valueOrField);
  };

  const validateForm = () => {
    const errors = {};

    // 🔹 Required field validation (respects f.required)
    insertionOrderFormFields(dropDownData.publisherData.options).forEach(
      (f) => {
        if (f.required && !formState.values[f.name]) {
          errors[f.name] = `${f.label} is required`;
        }
      },
    );

    const { startDate, document } = formState.values;

    // 🔹 Document validation
    if (!document) {
      errors.document = "Document is required.";
    }

    setFormState((prev) => ({ ...prev, errors }));
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setShowLoader(true);
    try {
      const payload = {
        name: formState.values.ioName,
        publisherID: Number(formState.values.publisher),
        documentStatus: formState?.values.status ? "SIGNED" : "UNSIGNED",
        version: "1",
      };

      // ✅ add startDate only if selected
      if (formState.values.startDate) {
        payload.startDate = moment
          .utc(formState.values.startDate, "YYYY-MM-DD")
          .startOf("day")
          .format("YYYY-MM-DD[T]HH:mm:ss[Z]");
      }

      // ✅ add endDate only if selected
      if (formState.values.endDate) {
        payload.endDate = moment
          .utc(formState.values.endDate, "YYYY-MM-DD")
          .endOf("day")
          .format("YYYY-MM-DD[T]HH:mm:ss[Z]");
      }

      const formData = new FormData();

      formData.append("ioData", JSON.stringify(payload));

      // IMPORTANT: same key as backend / Postman
      if (Array.isArray(formState.values.document)) {
        formState.values.document.forEach((file) => {
          formData.append("pdf", file);
        });
      } else {
        formData.append("pdf", formState.values.document);
      }

      const res = await createInsertionOrder(formData);

      if (res.code === 200) {
        notify("success", res?.message);
        getInsertionOrderDetailsByID();
        setShowLoader(false);
        setModalOpen(false);
        setSelected(null);
      } else {
        const message = Array.isArray(res?.message)
          ? res?.message[0]?.Msg
          : res?.message || "Something went wrong";
        notify("danger", message);
        // setshowAddMdoal({
        //   formType: "Create New Deal",
        //   show: false,
        // });
        setShowLoader(false);
      }
    } catch (err) {
      setShowLoader(false);
      notify("danger", "Upload failed");
    }
  };

  return (
    <>
      <div className="rna-container">
        <NotificationAlert ref={notificationAlertRef} />
      </div>
      <Container fluid className="mt--6">
        <Card className="shadow-sm">
          <Breadcrumbs />
          <ReactBsTable
            title={`Insertion Orders Details`}
            description={
              publisher
                ? `Insertion orders for ${publisher?.publisher?.companyName || publisher?.publisher?.publisherName || "Publisher"}`
                : "Insertion orders"
            }
            data={ioData}
            columns={ioColumns}
            keyField="id"
            addbtntext="Add IO"
            showAddbtn={true}
            enableRowClick={true}
            onRowClick={handleRowClick}
            onAddBtnClick={() => {
              setSelected(null);
              setFormState({
                values: {
                  status: true,
                  publisher: ioParams?.publisher?.publisherID,
                },
                errors: {},
              });
              setModalOpen(true);
              getDropDownData();
            }}
            page={pageNum}
            sizePerPage={pageSize}
            totalRecords={totalRecords}
            onPageChange={handlePageChange}
            onSortChange={handleSortChange}
            handleSizePerPageChange={handleSizePerPageChange}
            enablePageSizeManagement={true}
            defaultSortField={sortConfig.dataField}
            defaultSortOrder={sortConfig.sortOrder}
            showLoader={showLoader}
          />
        </Card>

        {/* ADD / EDIT IO MODAL */}
        <DynamicFormModal
          isOpen={modalOpen}
          toggle={() => setModalOpen(false)}
          title={isEditMode ? "Edit Insertion Order" : "Add Insertion Order"}
          fields={insertionOrderFormFields(
            dropDownData?.publisherData?.options,
            disabledPublisherDropdown,
          )}
          formData={formState.values}
          errors={formState.errors}
          onChange={handleChange}
          onSubmit={handleSubmit}
          showDocumentUpload={true}
          formState={formState}
          setFormState={setFormState}
          isEditMode={isEditMode}
          trueLabel={INSERTION_ORDER_STATUS_LABELS.TRUE}
          falseLabel={INSERTION_ORDER_STATUS_LABELS.FALSE}
          showLoader={showLoader}
        />

        {/* Document Preview Modal */}
        <Modal
          isOpen={previewModal}
          toggle={closePreviewModal}
          className="modal-xl"
          style={{ maxWidth: "90%" }}
        >
          <ModalHeader toggle={closePreviewModal}>
            {previewTitle}
            <Button
              // color="info"
              size="sm"
              className="btn-gradient-primary ml-2"
              onClick={downloadFromPreview}
            >
              <i className="fa fa-download mr-1" /> Download
            </Button>
          </ModalHeader>
          <ModalBody style={{ height: "70vh", padding: 0 }}>
            {previewUrl ? (
              <iframe
                src={previewUrl}
                title="Document Preview"
                style={{
                  width: "100%",
                  height: "100%",
                  border: "none",
                }}
                onError={(e) => {
                  console.error("Iframe loading error:", e);
                  e.target.innerHTML = `
                    <div style="display: flex; justify-content: center; align-items: center; height: 100%;">
                      <div style="text-align: center;">
                        <p>Unable to load document preview.</p>
                        <button onclick="window.open('${previewUrl}', '_blank')" 
                                style="padding: 8px 16px; background: #007bff; color: white; border: none; border-radius: 4px; cursor: pointer;">
                          Open in new tab
                        </button>
                      </div>
                    </div>
                  `;
                }}
              />
            ) : (
              <div className="text-center p-5">
                <p>No document available for preview</p>
              </div>
            )}
          </ModalBody>
        </Modal>
      </Container>
    </>
  );
}

export default InsertionOrderDetails;